import {
  type CollisionDetection,
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { RecursPeriod as RecursPeriodBase } from '@goaltree/backend/convex/lib/schedule'
import { cn } from '@goaltree/ui/lib/utils'
import i18next from 'i18next'
import { ChevronLeft, ChevronRight, GripVertical, Layers, RefreshCw } from 'lucide-react'
import {
  type CSSProperties,
  type MutableRefObject,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { haptic } from '@/lib/haptics'
import { dueOnDate } from '@/lib/schedule'
import { useViewStore } from '@/store/view.store'

// Per-task set of completed local-day strings ('YYYY-MM-DD'), so recurring
// occurrences render as done on the days they were actually ticked off.
const CompletionDatesContext = createContext<Map<string, Set<string>>>(new Map())

type RecursPeriod = RecursPeriodBase

export type CalendarTask = InboxTask

type InboxTask = {
  _id: Id<'nodes'>
  title: string
  completedAt: number | null
  effort: 'low' | 'medium' | 'high'
  recurs?: boolean
  recursPeriod?: RecursPeriod
  recursDays?: number[]
  recurrenceDay?: number
  recurrenceTime?: string
  dueDate?: number
  durationMinutes?: number
  allDay?: boolean
  status?: 'backlog' | 'in-progress' | 'done'
  parentId: Id<'nodes'> | null
  description?: string
  // Set on items from another goal in "all goals" mode: tint + hover label.
  isHabit?: boolean
  isInbox?: boolean
  goalId?: Id<'nodes'>
  goalTitle?: string
  goalCategory?: string
  goalColor?: string
}

function goalTint(task: InboxTask, isDone: boolean): CSSProperties | undefined {
  if (!task.goalColor || isDone) return undefined
  return {
    backgroundColor: `color-mix(in srgb, ${task.goalColor} 22%, transparent)`,
    boxShadow: `inset 3px 0 0 ${task.goalColor}`,
  }
}
function chipTitle(task: InboxTask): string | undefined {
  return task.goalTitle ? `${task.goalTitle} · ${task.title}` : undefined
}

// A task is all-day when explicitly flagged, or (legacy) when it recurs with no
// time set. Flagged tasks win even if they still carry a dueDate/recurrenceTime.
function isAllDay(task: InboxTask): boolean {
  return !!task.allDay || (!task.recurrenceTime && !task.dueDate)
}

type CalendarView = 'month' | 'week' | 'day'

// Live drop-target preview while dragging a timed block: which day column (key),
// the snapped minutes-of-day it will land at, and its duration (for band height).
type DropSlot = { key: string; minutes: number; durationMin: number } | null

// ─── constants ────────────────────────────────────────────────────────────────

const HOUR_HEIGHT = 56 // px per hour
const TIME_COL_W = 52 // px for the left time label column
const HOURS = Array.from({ length: 24 }, (_, i) => i)

// Drag-to-schedule (via @dnd-kit): tray chips are draggable, day cells/columns
// are droppables keyed by this day string.
const TRAY_DROPPABLE = 'unscheduled-tray'
// The tray sits (sticky) over the full-height day columns, so the pointer is
// within both — prefer the tray when it's under the pointer, else normal.
const trayFirstCollision: CollisionDetection = (args) => {
  const trayHit = pointerWithin({
    ...args,
    droppableContainers: args.droppableContainers.filter((c) => c.id === TRAY_DROPPABLE),
  })
  return trayHit.length > 0 ? trayHit : pointerWithin(args)
}
function dayKey(day: Date): string {
  return `day:${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`
}
function parseDayKey(key: string): Date {
  const [y, m, d] = key.replace('day:', '').split('-').map(Number)
  return new Date(y, m, d)
}
// Separate prefix (no 'day' substring, so parseDayKey can't mangle it) for the
// all-day row's per-day drop targets — dropping here flags the task all-day.
const ALL_DAY_PREFIX = 'ad:'
function allDayKey(day: Date): string {
  return `${ALL_DAY_PREFIX}${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`
}
function parseAllDayKey(key: string): Date {
  const [y, m, d] = key.slice(ALL_DAY_PREFIX.length).split('-').map(Number)
  return new Date(y, m, d)
}
// Timed blocks suffix their draggable id with the day (so a recurring task's
// per-column instances stay unique); strip it back to the task id.
function taskIdOf(dragId: string | number): string {
  return String(dragId).split('__')[0]
}

// ─── date helpers ─────────────────────────────────────────────────────────────

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

export function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function startOfWeek(d: Date): Date {
  const result = new Date(d)
  result.setDate(d.getDate() - d.getDay())
  result.setHours(0, 0, 0, 0)
  return result
}

function addDays(d: Date, n: number): Date {
  const result = new Date(d)
  result.setDate(d.getDate() + n)
  return result
}

function getCalendarGrid(year: number, month: number): (Date | null)[] {
  const firstDay = new Date(year, month, 1)
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const grid: (Date | null)[] = []
  for (let i = 0; i < firstDay.getDay(); i++) grid.push(null)
  for (let d = 1; d <= daysInMonth; d++) grid.push(new Date(year, month, d))
  while (grid.length % 7 !== 0) grid.push(null)
  return grid
}

function taskAppearsOnDay(task: InboxTask, day: Date): boolean {
  if (task.dueDate && !task.recursPeriod && !task.recurs) {
    return sameDay(new Date(task.dueDate), day)
  }

  const period = task.recursPeriod ?? (task.recurs ? 'daily' : null)
  if (!period || period === 'everyOtherDay') return false
  return dueOnDate(period, task.recurrenceDay, day, task.recursDays)
}

// For recurring tasks, an occurrence is done when it was ticked off for that
// specific day — either the live completedAt lands on it, or the per-day
// completion log (doneDates) records it. Non-recurring tasks are globally done
// once completedAt is set.
function isTaskDoneForDay(task: InboxTask, day: Date, doneDates?: Set<string>): boolean {
  if (!task.recursPeriod && !task.recurs) return !!task.completedAt
  if (doneDates?.has(localDateStr(day))) return true
  return !!task.completedAt && sameDay(new Date(task.completedAt), day)
}

function parseTime(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return (h ?? 0) + (m ?? 0) / 60
}

// Pack overlapping time blocks into side-by-side columns (Google Calendar style).
// Returns per-id { col, cols } so each block can be positioned as a vertical lane.
function computeOverlapLayout(
  items: { id: string; start: number; end: number }[],
): Map<string, { col: number; cols: number }> {
  const layout = new Map<string, { col: number; cols: number }>()
  const sorted = [...items].sort((a, b) => a.start - b.start || a.end - b.end)
  let cluster: typeof sorted = []
  let clusterEnd = -Infinity
  const place = () => {
    const colEnds: number[] = []
    const colOf = new Map<string, number>()
    for (const e of cluster) {
      let c = colEnds.findIndex((end) => end <= e.start + 1e-9)
      if (c === -1) {
        c = colEnds.length
        colEnds.push(e.end)
      } else {
        colEnds[c] = e.end
      }
      colOf.set(e.id, c)
    }
    for (const e of cluster) layout.set(e.id, { col: colOf.get(e.id) ?? 0, cols: colEnds.length })
  }
  for (const e of sorted) {
    if (cluster.length && e.start >= clusterEnd - 1e-9) {
      place()
      cluster = []
      clusterEnd = -Infinity
    }
    cluster.push(e)
    clusterEnd = Math.max(clusterEnd, e.end)
  }
  if (cluster.length) place()
  return layout
}

function formatHour(h: number): string {
  if (h === 0) return '12 AM'
  if (h < 12) return `${h} AM`
  if (h === 12) return '12 PM'
  return `${h - 12} PM`
}

function formatMinutes(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`
}

// ─── task chip (month view) ───────────────────────────────────────────────────

function TaskChip({
  task,
  day,
  onComplete: _onComplete,
  onUncomplete: _onUncomplete,
  onDetail,
  compact = false,
}: {
  task: InboxTask
  day?: Date
  onComplete: (id: Id<'nodes'>) => void
  onUncomplete: (id: Id<'nodes'>) => void
  onDetail: (task: InboxTask, day?: Date) => void
  compact?: boolean
}) {
  const doneDates = useContext(CompletionDatesContext)
  const isDone = day
    ? isTaskDoneForDay(task, day, doneDates.get(task._id as string))
    : !!task.completedAt
  const isRecurring = !!(task.recursPeriod || task.recurs)
  return (
    <button
      type="button"
      onClick={() => onDetail(task, day)}
      title={chipTitle(task)}
      style={goalTint(task, isDone)}
      className={cn(
        'flex w-full cursor-pointer items-center gap-1 rounded-md px-1.5 text-left leading-tight transition-colors',
        compact ? 'py-0.5 text-[10px]' : 'py-1 text-xs',
        isDone
          ? 'text-muted-foreground/50 line-through'
          : task.goalColor
            ? 'text-foreground hover:brightness-95 dark:hover:brightness-125'
            : task.effort === 'high'
              ? 'bg-destructive/8 text-foreground hover:bg-destructive/15'
              : 'bg-primary/8 text-foreground hover:bg-primary/15',
      )}
    >
      {isRecurring && (
        <RefreshCw className="h-2.5 w-2.5 shrink-0 text-primary-text/50" strokeWidth={2.5} />
      )}
      {task.effort === 'high' && !isRecurring && (
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-destructive/60" />
      )}
      <span className="truncate">{task.title}</span>
    </button>
  )
}

// ─── day cell (month grid) ────────────────────────────────────────────────────

function DayCell({
  day,
  tasks,
  isToday,
  isCurrentMonth = true,
  maxChips,
  onComplete,
  onUncomplete,
  onDetail,
  reschedulable = false,
  isPast = false,
  compact = false,
}: {
  day: Date
  tasks: InboxTask[]
  isToday: boolean
  isCurrentMonth?: boolean
  maxChips: number
  onComplete: (id: Id<'nodes'>) => void
  onUncomplete: (id: Id<'nodes'>) => void
  onDetail: (task: InboxTask, day?: Date) => void
  reschedulable?: boolean
  isPast?: boolean
  compact?: boolean
}) {
  const doneDates = useContext(CompletionDatesContext)
  const { setNodeRef, isOver } = useDroppable({ id: dayKey(day) })
  const sorted = [
    ...tasks.filter((t) => !isTaskDoneForDay(t, day, doneDates.get(t._id as string))),
    ...tasks.filter((t) => isTaskDoneForDay(t, day, doneDates.get(t._id as string))),
  ]
  const visible = sorted.slice(0, maxChips)
  const overflow = sorted.length - visible.length
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex flex-col gap-0.5 bg-card p-1.5',
        !isCurrentMonth && 'opacity-40',
        isOver && !isPast && 'ring-2 ring-inset ring-primary',
      )}
    >
      <span
        className={cn(
          'self-start flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-medium leading-none mb-0.5',
          isToday ? 'bg-primary text-primary-foreground' : 'text-foreground/60',
        )}
      >
        {day.getDate()}
      </span>
      {visible.map((t) => {
        const chip = (
          <TaskChip
            task={t}
            day={day}
            onComplete={onComplete}
            onUncomplete={onUncomplete}
            onDetail={onDetail}
            compact={compact}
          />
        )
        // Non-recurring dated chips can be dragged to another day to reschedule.
        return reschedulable && !(t.recursPeriod || t.recurs) ? (
          <DraggableGridChip key={t._id as string} id={t._id as string}>
            {chip}
          </DraggableGridChip>
        ) : (
          <div key={t._id as string}>{chip}</div>
        )
      })}
      {overflow > 0 && (
        <span className="px-1 text-[9px] text-muted-foreground/60">+{overflow}</span>
      )}
    </div>
  )
}

// ─── month view ───────────────────────────────────────────────────────────────

function MonthView({
  year,
  month,
  tasks,
  today,
  onComplete,
  onUncomplete,
  onDetail,
  reschedulable = false,
}: {
  year: number
  month: number
  tasks: InboxTask[]
  today: Date
  onComplete: (id: Id<'nodes'>) => void
  onUncomplete: (id: Id<'nodes'>) => void
  onDetail: (task: InboxTask, day?: Date) => void
  reschedulable?: boolean
}) {
  const grid = useMemo(() => getCalendarGrid(year, month), [year, month])
  const dayLabels = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const date = new Date(2025, 0, 5 + i)
        return new Intl.DateTimeFormat(i18next.language, { weekday: 'short' }).format(date)
      }),
    [],
  )

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="grid grid-cols-7 border-b border-border/40">
        {dayLabels.map((label) => (
          <div
            key={label}
            className="py-2 text-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/60"
          >
            {label}
          </div>
        ))}
      </div>
      <div className="flex-1 grid grid-cols-7 auto-rows-fr divide-x divide-y divide-border/30">
        {grid.map((day, i) =>
          !day ? (
            <div key={`e-${i}`} className="bg-muted/10" />
          ) : (
            <DayCell
              key={day.toISOString()}
              day={day}
              tasks={tasks.filter((t) => taskAppearsOnDay(t, day))}
              isToday={sameDay(day, today)}
              isCurrentMonth={day.getMonth() === month}
              isPast={day.getTime() < today.getTime()}
              maxChips={3}
              onComplete={onComplete}
              onUncomplete={onUncomplete}
              onDetail={onDetail}
              reschedulable={reschedulable}
              compact
            />
          ),
        )}
      </div>
    </div>
  )
}

// ─── time grid (week + day) ───────────────────────────────────────────────────

function AllDayCell({
  day,
  tasks,
  droppable,
  onComplete,
  onUncomplete,
  onDetail,
  singleDay,
}: {
  day: Date
  tasks: InboxTask[]
  droppable: boolean
  onComplete: (id: Id<'nodes'>) => void
  onUncomplete: (id: Id<'nodes'>) => void
  onDetail: (task: InboxTask, day?: Date) => void
  singleDay: boolean
}) {
  const { setNodeRef, isOver } = useDroppable({ id: allDayKey(day), disabled: !droppable })
  const allDayTasks = tasks.filter((t) => taskAppearsOnDay(t, day) && isAllDay(t))
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex-1 min-w-0 border-l border-border/20 p-0.5 flex flex-col gap-0.5 transition-colors',
        isOver && 'bg-primary/10 ring-1 ring-inset ring-primary/40',
      )}
      style={{ minWidth: singleDay ? 0 : 36 }}
    >
      {allDayTasks.map((task) => (
        <TaskChip
          key={task._id as string}
          task={task}
          day={day}
          onComplete={onComplete}
          onUncomplete={onUncomplete}
          onDetail={onDetail}
          compact
        />
      ))}
      {allDayTasks.length === 0 && <div className="h-5" />}
    </div>
  )
}

function AllDayRow({
  days,
  tasks,
  droppable,
  onComplete,
  onUncomplete,
  onDetail,
}: {
  days: Date[]
  tasks: InboxTask[]
  droppable: boolean
  onComplete: (id: Id<'nodes'>) => void
  onUncomplete: (id: Id<'nodes'>) => void
  onDetail: (task: InboxTask, day?: Date) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex shrink-0 border-b border-border/40">
      <div
        className="sticky left-0 z-10 shrink-0 flex items-start justify-end bg-background pt-1.5 pr-2"
        style={{ width: TIME_COL_W }}
      >
        <span className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground/40">
          {t('inbox.calendarAllDay')}
        </span>
      </div>
      {days.map((day) => (
        <AllDayCell
          key={day.toISOString()}
          day={day}
          tasks={tasks}
          droppable={droppable}
          onComplete={onComplete}
          onUncomplete={onUncomplete}
          onDetail={onDetail}
          singleDay={days.length === 1}
        />
      ))}
    </div>
  )
}

function DayColumn({
  day,
  tasks,
  today,
  onComplete: _onComplete,
  onUncomplete: _onUncomplete,
  onDetail,
  onDurationChange,
  reschedulable = false,
  dropSlot = null,
}: {
  day: Date
  tasks: InboxTask[]
  today: Date
  onComplete: (id: Id<'nodes'>) => void
  onUncomplete: (id: Id<'nodes'>) => void
  onDetail: (task: InboxTask, day?: Date) => void
  onDurationChange?: (id: Id<'nodes'>, minutes: number) => void
  reschedulable?: boolean
  dropSlot?: DropSlot
}) {
  const doneDates = useContext(CompletionDatesContext)
  const { setNodeRef, isOver } = useDroppable({ id: dayKey(day) })
  const drop = dropSlot && dropSlot.key === dayKey(day) ? dropSlot : null
  const now = new Date()
  const isToday = sameDay(day, today)
  const isPast = day.getTime() < today.getTime()
  const currentTimeFraction = isToday ? now.getHours() + now.getMinutes() / 60 : null
  const timedTasks = tasks.filter(
    (t) => taskAppearsOnDay(t, day) && !isAllDay(t) && (!!t.recurrenceTime || !!t.dueDate),
  )

  const [resizing, setResizing] = useState<{
    id: Id<'nodes'>
    startY: number
    startMinutes: number
    currentMinutes: number
  } | null>(null)
  const justResizedRef = useRef(false)

  useEffect(() => {
    if (!resizing) return
    const onMove = (e: MouseEvent | TouchEvent) => {
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
      const dy = clientY - resizing.startY
      const snapped = Math.round(((dy / HOUR_HEIGHT) * 60) / 15) * 15
      setResizing((r) =>
        r ? { ...r, currentMinutes: Math.max(15, r.startMinutes + snapped) } : null,
      )
    }
    const onUp = () => {
      if (resizing.currentMinutes !== resizing.startMinutes) {
        onDurationChange?.(resizing.id, resizing.currentMinutes)
        justResizedRef.current = true
        setTimeout(() => {
          justResizedRef.current = false
        }, 300)
      }
      setResizing(null)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.addEventListener('touchmove', onMove, { passive: true })
    document.addEventListener('touchend', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.removeEventListener('touchmove', onMove)
      document.removeEventListener('touchend', onUp)
    }
  }, [resizing, onDurationChange])

  // Lay overlapping timed tasks out side-by-side so they don't stack.
  const overlapLayout = computeOverlapLayout(
    timedTasks.map((task) => {
      const start = task.recurrenceTime
        ? parseTime(task.recurrenceTime)
        : task.dueDate
          ? (() => {
              const dd = new Date(task.dueDate)
              return dd.getHours() + dd.getMinutes() / 60
            })()
          : 0
      const dur = resizing?.id === task._id ? resizing.currentMinutes : (task.durationMinutes ?? 60)
      return { id: task._id as string, start, end: start + dur / 60 }
    }),
  )

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex-1 relative border-l border-border/20',
        isToday && 'bg-primary/[0.02]',
        isOver && !isPast && 'bg-primary/[0.04]',
      )}
      style={{ minWidth: 36 }}
    >
      {/* Hour grid lines */}
      {HOURS.map((h) => (
        <div
          key={h}
          className="absolute w-full border-t border-border/10 pointer-events-none"
          style={{ top: h * HOUR_HEIGHT }}
        />
      ))}

      {/* Drop-target preview — the block's full footprint at the snapped time */}
      {drop && (
        <div
          className="pointer-events-none absolute inset-x-0 z-30"
          style={{
            top: (drop.minutes / 60) * HOUR_HEIGHT,
            height: Math.max(HOUR_HEIGHT / 4, (drop.durationMin / 60) * HOUR_HEIGHT),
          }}
        >
          <div className="h-full rounded-md border-2 border-primary bg-primary/20" />
          <span className="absolute -top-2 left-0 rounded bg-primary px-1 text-[9px] font-bold leading-tight text-primary-foreground">
            {formatMinutes(drop.minutes)}
          </span>
        </div>
      )}

      {/* Current time indicator */}
      {currentTimeFraction !== null && (
        <div
          className="absolute w-full z-20 pointer-events-none"
          style={{ top: currentTimeFraction * HOUR_HEIGHT }}
        >
          <div className="absolute -left-1 top-[-3px] h-1.5 w-1.5 rounded-full bg-primary" />
          <div className="border-t border-primary w-full" />
        </div>
      )}

      {/* Timed task blocks */}
      {timedTasks.map((task) => {
        const d = task.recurrenceTime ? null : task.dueDate ? new Date(task.dueDate) : null
        const timeFraction = task.recurrenceTime
          ? parseTime(task.recurrenceTime)
          : d
            ? d.getHours() + d.getMinutes() / 60
            : 0
        const timeLabel =
          task.recurrenceTime ??
          (d
            ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
            : '')
        const isDone = isTaskDoneForDay(task, day, doneDates.get(task._id as string))
        const isRecurring = !!(task.recursPeriod || task.recurs)
        const durationMins =
          resizing?.id === task._id ? resizing.currentMinutes : (task.durationMinutes ?? 60)
        const blockHeight = (durationMins / 60) * HOUR_HEIGHT
        const durationLabel =
          durationMins < 60
            ? `${durationMins}m`
            : durationMins % 60 === 0
              ? `${durationMins / 60}h`
              : `${Math.floor(durationMins / 60)}h ${durationMins % 60}m`
        const { col, cols } = overlapLayout.get(task._id as string) ?? { col: 0, cols: 1 }
        return (
          <TimedBlock
            key={task._id as string}
            task={task}
            day={day}
            style={{
              top: timeFraction * HOUR_HEIGHT + 1,
              height: blockHeight - 2,
              left: `calc(${(col / cols) * 100}% + 2px)`,
              width: `calc(${100 / cols}% - 4px)`,
            }}
            isDone={isDone}
            isRecurring={isRecurring}
            timeLabel={timeLabel}
            durationLabel={durationLabel}
            durationMins={durationMins}
            reschedulable={reschedulable}
            onDetail={onDetail}
            onDurationChange={onDurationChange}
            onResizeStart={(startY) =>
              setResizing({
                id: task._id,
                startY,
                startMinutes: task.durationMinutes ?? 60,
                currentMinutes: task.durationMinutes ?? 60,
              })
            }
            justResizedRef={justResizedRef}
          />
        )
      })}
    </div>
  )
}

// A single timed block in the week/day grid. Draggable (to reschedule to another
// day) when reschedulable + not recurring/done; the resize handle is a sibling
// of the drag activator so the two gestures don't collide.
function TimedBlock({
  task,
  day,
  style,
  isDone,
  isRecurring,
  timeLabel,
  durationLabel,
  durationMins,
  reschedulable,
  onDetail,
  onDurationChange,
  onResizeStart,
  justResizedRef,
}: {
  task: InboxTask
  day: Date
  style: CSSProperties
  isDone: boolean
  isRecurring: boolean
  timeLabel: string
  durationLabel: string
  durationMins: number
  reschedulable: boolean
  onDetail: (task: InboxTask, day?: Date) => void
  onDurationChange?: (id: Id<'nodes'>, minutes: number) => void
  onResizeStart: (startY: number) => void
  justResizedRef: MutableRefObject<boolean>
}) {
  // Recurring habits are draggable too (to change their time/day), just not
  // when done for that occurrence.
  const draggable = reschedulable && !isDone
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${task._id}__${dayKey(day)}`,
    disabled: !draggable,
  })
  return (
    <div className="absolute z-10" style={style}>
      <button
        ref={setNodeRef}
        type="button"
        {...(draggable ? listeners : {})}
        {...(draggable ? attributes : {})}
        onClick={() => {
          if (justResizedRef.current) return
          onDetail(task, day)
        }}
        title={chipTitle(task)}
        style={goalTint(task, isDone)}
        className={cn(
          'w-full h-full cursor-pointer rounded-md px-1.5 py-1 text-left text-[11px] leading-snug transition-colors overflow-hidden',
          draggable && 'touch-none',
          isDragging && 'opacity-40',
          isDone
            ? 'bg-muted/50 text-muted-foreground/50 line-through'
            : task.goalColor
              ? 'text-foreground border border-border/40 hover:brightness-95 dark:hover:brightness-125'
              : task.effort === 'high'
                ? 'bg-destructive/15 text-foreground hover:bg-destructive/25 border border-destructive/20'
                : 'bg-primary/15 text-foreground hover:bg-primary/25 border border-primary/20',
        )}
      >
        <span className="flex items-center gap-1 font-medium">
          {isRecurring && (
            <RefreshCw className="h-2.5 w-2.5 shrink-0 opacity-60" strokeWidth={2.5} />
          )}
          <span className="truncate">{task.title}</span>
        </span>
        <span className="text-[9px] opacity-60">
          {timeLabel}
          {durationMins !== 60 && ` · ${durationLabel}`}
        </span>
      </button>
      {/* Drag-to-resize handle (separate gesture from the reschedule drag) */}
      {!isDone && onDurationChange && (
        <div
          className="absolute bottom-0 left-0 right-0 h-3 flex items-end justify-center pb-0.5 cursor-ns-resize z-20 select-none"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onResizeStart(e.clientY)
          }}
          onTouchStart={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onResizeStart(e.touches[0].clientY)
          }}
        >
          <div className="w-5 h-0.5 rounded-full bg-current opacity-25" />
        </div>
      )}
    </div>
  )
}

function TimeGrid({
  days,
  tasks,
  today,
  onComplete,
  onUncomplete,
  onDetail,
  onDurationChange,
  reschedulable = false,
  allDayDroppable = false,
  dropSlot = null,
  dayHeaderTop = 'top-11',
}: {
  days: Date[]
  tasks: InboxTask[]
  today: Date
  onComplete: (id: Id<'nodes'>) => void
  onUncomplete: (id: Id<'nodes'>) => void
  onDetail: (task: InboxTask, day?: Date) => void
  onDurationChange?: (id: Id<'nodes'>, minutes: number) => void
  reschedulable?: boolean
  allDayDroppable?: boolean
  dropSlot?: DropSlot
  dayHeaderTop?: string
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const dayLabels = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const date = new Date(2025, 0, 5 + i)
        return new Intl.DateTimeFormat(i18next.language, { weekday: 'short' }).format(date)
      }),
    [],
  )

  return (
    <div className="flex flex-col">
      {/* Sticky day headers — stack right below the sticky toolbar (h-11) */}
      <div
        className={cn(
          'sticky z-30 flex shrink-0 border-b border-border/40 bg-background',
          dayHeaderTop,
        )}
      >
        <div className="sticky left-0 shrink-0 bg-background" style={{ width: TIME_COL_W }} />
        {days.map((day) => {
          const isToday = sameDay(day, today)
          return (
            <div
              key={day.toISOString()}
              className={cn(
                'flex-1 flex flex-col items-center gap-0.5 py-2 border-l border-border/20',
                isToday && 'bg-primary/5',
              )}
              style={{ minWidth: days.length === 1 ? 0 : 36 }}
            >
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/60">
                {dayLabels[day.getDay()]}
              </span>
              <span
                className={cn(
                  'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
                  isToday ? 'bg-primary text-primary-foreground' : 'text-foreground/70',
                )}
              >
                {day.getDate()}
              </span>
            </div>
          )
        })}
      </div>

      {/* All-day row */}
      <AllDayRow
        days={days}
        tasks={tasks}
        droppable={allDayDroppable}
        onComplete={onComplete}
        onUncomplete={onUncomplete}
        onDetail={onDetail}
      />

      {/* Time grid — no internal vertical scroll; the outer inbox container
          scrolls. Horizontal scroll kept for narrow multi-day weeks. */}
      <div ref={scrollRef} className="overflow-x-auto">
        <div className="flex" style={{ height: 24 * HOUR_HEIGHT }}>
          {/* Sticky time labels */}
          <div
            className="sticky left-0 shrink-0 bg-background z-10 border-r border-border/20"
            style={{ width: TIME_COL_W }}
          >
            {HOURS.map((h) => (
              <div
                key={h}
                className="absolute flex items-start justify-end w-full pr-2"
                style={{ top: h * HOUR_HEIGHT, height: HOUR_HEIGHT }}
              >
                <span className="text-[10px] text-muted-foreground/50 -mt-[6px] tabular-nums">
                  {formatHour(h)}
                </span>
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day) => (
            <DayColumn
              key={day.toISOString()}
              day={day}
              tasks={tasks}
              today={today}
              onComplete={onComplete}
              onUncomplete={onUncomplete}
              onDetail={onDetail}
              onDurationChange={onDurationChange}
              reschedulable={reschedulable}
              dropSlot={dropSlot}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── week view ────────────────────────────────────────────────────────────────

function WeekView({
  weekStart,
  tasks,
  today,
  onComplete,
  onUncomplete,
  onDetail,
  onDurationChange,
  reschedulable = false,
  allDayDroppable = false,
  dropSlot = null,
  dayHeaderTop,
}: {
  weekStart: Date
  tasks: InboxTask[]
  today: Date
  onComplete: (id: Id<'nodes'>) => void
  onUncomplete: (id: Id<'nodes'>) => void
  onDetail: (task: InboxTask, day?: Date) => void
  onDurationChange?: (id: Id<'nodes'>, minutes: number) => void
  reschedulable?: boolean
  allDayDroppable?: boolean
  dropSlot?: DropSlot
  dayHeaderTop?: string
}) {
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  )
  return (
    <TimeGrid
      days={days}
      tasks={tasks}
      today={today}
      onComplete={onComplete}
      onUncomplete={onUncomplete}
      onDetail={onDetail}
      onDurationChange={onDurationChange}
      reschedulable={reschedulable}
      allDayDroppable={allDayDroppable}
      dropSlot={dropSlot}
      dayHeaderTop={dayHeaderTop}
    />
  )
}

// ─── day view ─────────────────────────────────────────────────────────────────

function DayView({
  day,
  tasks,
  today,
  onComplete,
  onUncomplete,
  onDetail,
  onDurationChange,
  reschedulable = false,
  allDayDroppable = false,
  dropSlot = null,
  dayHeaderTop,
}: {
  day: Date
  tasks: InboxTask[]
  today: Date
  onComplete: (id: Id<'nodes'>) => void
  onUncomplete: (id: Id<'nodes'>) => void
  onDetail: (task: InboxTask, day?: Date) => void
  onDurationChange?: (id: Id<'nodes'>, minutes: number) => void
  reschedulable?: boolean
  allDayDroppable?: boolean
  dropSlot?: DropSlot
  dayHeaderTop?: string
}) {
  return (
    <TimeGrid
      days={[day]}
      tasks={tasks}
      today={today}
      onComplete={onComplete}
      onUncomplete={onUncomplete}
      onDetail={onDetail}
      onDurationChange={onDurationChange}
      reschedulable={reschedulable}
      allDayDroppable={allDayDroppable}
      dropSlot={dropSlot}
      dayHeaderTop={dayHeaderTop}
    />
  )
}

// ─── draggable wrappers ─────────────────────────────────────────────────────

// Wraps an already-placed calendar chip so it can be dragged to another day.
function DraggableGridChip({ id, children }: { id: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id })
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn('touch-none', isDragging && 'opacity-40')}
    >
      {children}
    </div>
  )
}

// The unscheduled tray. A droppable (so scheduled tasks can be dragged back to
// clear their due date) — must live INSIDE the DndContext to register.
function UnscheduledTray({
  unscheduled,
  onDetail,
  // Habits can't be unscheduled, so don't advertise the tray as a drop target
  // (no highlight, no "drop to unschedule" hint) while dragging one.
  habitDrag = false,
  rail = false,
}: {
  unscheduled: InboxTask[]
  onDetail: (task: InboxTask, day?: Date) => void
  habitDrag?: boolean
  // Desktop side rail: a vertical list in a card instead of the sticky strip.
  rail?: boolean
}) {
  const { t } = useTranslation()
  const { setNodeRef, isOver } = useDroppable({ id: TRAY_DROPPABLE })
  if (rail)
    return (
      <div
        ref={setNodeRef}
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-2 rounded-2xl border bg-card/60 px-3.5 py-3 transition-colors',
          isOver && !habitDrag ? 'border-primary bg-primary/5' : 'border-border',
        )}
      >
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          {t('rails.unscheduled', { count: unscheduled.length })}
        </p>
        {unscheduled.length > 0 ? (
          <div className="-mx-1 flex min-h-0 flex-col gap-1.5 overflow-y-auto overscroll-contain px-1 pb-0.5">
            {unscheduled.map((task) => (
              <DraggableTrayChip key={task._id as string} task={task} onOpen={onDetail} wide />
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">{t('rails.unscheduledEmpty')}</p>
        )}
      </div>
    )
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'sticky bottom-0 z-30 shrink-0 border-t bg-background px-4 py-2 transition-colors',
        isOver && !habitDrag ? 'border-primary bg-primary/5' : 'border-border/40',
      )}
    >
      {unscheduled.length > 0 ? (
        <>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/60">
            {t('inbox.calendarUnscheduled', { count: unscheduled.length })}
          </p>
          {/* Only each chip's grip handle carries touch-none/drag listeners, so
              swiping anywhere else scrolls the tray freely. overscroll-contain
              keeps that scroll from bleeding into the page behind. */}
          <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto overscroll-contain pb-0.5 [touch-action:pan-y] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {unscheduled.map((task) => (
              <DraggableTrayChip key={task._id as string} task={task} onOpen={onDetail} />
            ))}
          </div>
        </>
      ) : habitDrag ? null : (
        <p className="py-1 text-center text-[10px] font-medium text-muted-foreground/50">
          {t('inbox.calendarDropToUnschedule')}
        </p>
      )}
    </div>
  )
}

function DraggableTrayChip({
  task,
  onOpen,
  wide = false,
}: {
  task: InboxTask
  onOpen: (task: InboxTask) => void
  wide?: boolean
}) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task._id as string })
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex shrink-0 items-center rounded-md border pr-2 text-xs leading-tight transition-colors',
        wide ? 'w-full' : 'max-w-[180px]',
        isDragging && 'opacity-40',
        task.effort === 'high'
          ? 'border-destructive/30 bg-destructive/8'
          : 'border-border/60 bg-muted/40',
      )}
    >
      {/* Only the grip is a drag source (touch-none) — the rest of the chip taps
          to open and lets the tray scroll. */}
      <span
        {...listeners}
        {...attributes}
        aria-label={t('inbox.calendarDragHandle', { defaultValue: 'Drag to schedule' })}
        className="flex cursor-grab touch-none items-center self-stretch pl-1 pr-0.5 text-muted-foreground/40 hover:text-muted-foreground/70 active:cursor-grabbing"
      >
        <GripVertical className="h-3.5 w-3.5 shrink-0" />
      </span>
      <button
        type="button"
        onClick={() => onOpen(task)}
        className="flex min-w-0 flex-1 items-center py-1 pr-1 text-left"
      >
        <span className="truncate">{task.title}</span>
      </button>
    </div>
  )
}

// ─── main component ───────────────────────────────────────────────────────────

export function InboxCalendar({
  tasks,
  completionDates,
  onComplete,
  onUncomplete,
  onDetail,
  onDurationChange,
  unscheduled,
  onSchedule,
  onUnschedule,
  onRescheduleHabit,
  onSetAllDay,
  coverMode = false,
  trayHost,
  allGoals,
}: {
  tasks: InboxTask[]
  completionDates?: Map<string, Set<string>>
  onComplete: (id: Id<'nodes'>) => void
  onUncomplete: (id: Id<'nodes'>) => void
  onDetail: (task: InboxTask, day?: Date) => void
  onDurationChange?: (id: Id<'nodes'>, minutes: number) => void
  // When provided, undated tasks show in a tray and can be dragged onto a day
  // to set their due date (drives onSchedule). minutesOfDay is passed when a
  // week/day timed block is dragged vertically to a new time. Dragging a
  // scheduled task back onto the tray clears its due date (onUnschedule).
  unscheduled?: InboxTask[]
  onSchedule?: (taskId: Id<'nodes'>, day: Date, minutesOfDay?: number) => void
  onUnschedule?: (taskId: Id<'nodes'>) => void
  // Dragging a recurring habit block (week/day) updates its recurrence time and
  // (for weekly/monthly) its day — dueDate doesn't apply to habits.
  onRescheduleHabit?: (taskId: Id<'nodes'>, day: Date, minutesOfDay: number) => void
  // Dropping a task onto the week/day all-day row flags it all-day (no time).
  // For one-off tasks the day also becomes their due date; for habits the day
  // is ignored (the recurrence already picks the days).
  onSetAllDay?: (taskId: Id<'nodes'>, day: Date) => void
  // Goal-details cover layout: the nav bar is transparent/absolute, so the
  // sticky toolbar + day headers must sit below it instead of at the very top.
  coverMode?: boolean
  // Shown desktop rail to portal the tray into (still inside this DndContext),
  // replacing the bottom strip.
  trayHost?: HTMLElement | null
  // "All goals" toggle shown in the toolbar when provided.
  allGoals?: { on: boolean; onChange: (on: boolean) => void }
}) {
  const { t } = useTranslation()
  const now = new Date()
  const today = useMemo(() => new Date(now.getFullYear(), now.getMonth(), now.getDate()), [])

  const view = useViewStore((s) => s.calendarView)
  const setView = useViewStore((s) => s.setCalendarView)
  const [currentDate, setCurrentDate] = useState(today)

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const weekStart = startOfWeek(currentDate)

  function navigate(direction: -1 | 1) {
    setCurrentDate((d) => {
      if (view === 'month') return new Date(d.getFullYear(), d.getMonth() + direction, 1)
      if (view === 'week') return addDays(d, direction * 7)
      return addDays(d, direction)
    })
  }

  const headerLabel = useMemo(() => {
    if (view === 'month') {
      return new Intl.DateTimeFormat(i18next.language, { month: 'long', year: 'numeric' }).format(
        new Date(year, month, 1),
      )
    }
    if (view === 'week') {
      const weekEnd = addDays(weekStart, 6)
      const s = new Intl.DateTimeFormat(i18next.language, {
        month: 'short',
        day: 'numeric',
      }).format(weekStart)
      const e = new Intl.DateTimeFormat(i18next.language, {
        month: 'short',
        day: 'numeric',
      }).format(weekEnd)
      return `${s} – ${e}`
    }
    return new Intl.DateTimeFormat(i18next.language, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(currentDate)
  }, [view, year, month, weekStart, currentDate])

  const undated = useMemo(
    () => tasks.filter((t) => !t.dueDate && !t.recursPeriod && !t.recurs && !t.completedAt).length,
    [tasks],
  )

  // Drag-to-schedule via @dnd-kit. A short move (6px) starts the drag so a plain
  // click still opens the task; dropping on a day droppable sets its due date.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  // Active drag: the task + whether it's a timed block + the grabbed element's
  // size, so the overlay keeps the block's real dimensions while dragging.
  const [activeDrag, setActiveDrag] = useState<{
    task: InboxTask
    timed: boolean
    w: number
    h: number
  } | null>(null)
  // Track the raw pointer + the grab offset within the dragged block. dnd-kit's
  // `delta` is unreliable alongside DragOverlay, so we compute the drop time from
  // the live pointer position relative to the target column instead.
  const pointerYRef = useRef(0)
  const grabOffsetRef = useRef(0)
  // Live drop-target preview (which day column + snapped time) while dragging a
  // timed block, so the grid can show where it will land.
  const [dropSlot, setDropSlot] = useState<DropSlot>(null)
  // True while hovering a past day — forces a not-allowed cursor over the grid.
  const [cursorBlocked, setCursorBlocked] = useState(false)
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      pointerYRef.current = e.clientY
    }
    window.addEventListener('pointermove', onMove)
    return () => window.removeEventListener('pointermove', onMove)
  }, [])

  const dropMinutesFor = (overRectTop: number) => {
    const blockTop = pointerYRef.current - grabOffsetRef.current
    return Math.max(
      0,
      Math.min(24 * 60 - 15, Math.round(((blockTop - overRectTop) / HOUR_HEIGHT) * 4) * 15),
    )
  }
  // Week/day are time grids: any drop (tray or block) picks up a time-of-day.
  // Month drops set the day only. (Month cells and day columns share day: keys.)
  const isTimeGrid = view === 'week' || view === 'day'
  const findTask = (dragId: string | number) => {
    const id = taskIdOf(dragId)
    return (
      tasks.find((t) => (t._id as string) === id) ??
      unscheduled?.find((t) => (t._id as string) === id) ??
      null
    )
  }
  const fromTray = (dragId: string | number) =>
    !!unscheduled?.some((t) => (t._id as string) === taskIdOf(dragId))
  const nowMinutes = now.getHours() * 60 + now.getMinutes()
  // Whether a drop lands in the past — a past day, or (in a time grid) an
  // earlier time today. Drives the no-drop cursor + suppressed highlight.
  const isPastDrop = (dayK: string, overRectTop: number): boolean => {
    const day = parseDayKey(dayK)
    if (day.getTime() < today.getTime()) return true
    return (
      isTimeGrid && day.getTime() === today.getTime() && dropMinutesFor(overRectTop) < nowMinutes
    )
  }
  // Resolve the final drop, disallowing the past: scheduled tasks clamp to
  // today / the current time; tray tasks return to the tray (null → no drop).
  const resolveDrop = (
    dragId: string | number,
    dayK: string,
    overRectTop: number,
  ): { day: Date; minutes: number | null } | null => {
    const scheduled = !fromTray(dragId)
    let day = parseDayKey(dayK)
    let minutes = isTimeGrid ? dropMinutesFor(overRectTop) : null
    if (day.getTime() < today.getTime()) {
      if (!scheduled) return null
      day = today
    }
    if (minutes != null && day.getTime() === today.getTime() && minutes < nowMinutes) {
      if (!scheduled) return null
      minutes = nowMinutes
    }
    return { day, minutes }
  }
  const isHabitDrag = (dragId: string | number) => {
    const t = findTask(dragId)
    return !!(t?.recursPeriod || t?.recurs)
  }

  // In cover mode the toolbar/day-headers stick below the transparent nav bar
  // (mobile MobileScreenHeader ≈ safe-area + 3.5rem; desktop SiteHeader = 3rem).
  const toolbarTop = coverMode
    ? 'top-[calc(env(safe-area-inset-top,0px)+3.5rem)] lg:top-12'
    : 'top-(--app-hdr-h)'
  const dayHeaderTop = coverMode
    ? 'top-[calc(env(safe-area-inset-top,0px)+6.25rem)] lg:top-[5.75rem]'
    : 'top-[calc(var(--app-hdr-h)+2.75rem)]'

  return (
    <CompletionDatesContext.Provider value={completionDates ?? new Map()}>
      <DndContext
        sensors={sensors}
        collisionDetection={trayFirstCollision}
        // Re-measure droppables continuously so the tray (which appears only
        // during a non-habit drag) is registered as a drop target.
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={({ active, activatorEvent }) => {
          haptic()
          const task = findTask(active.id)
          const timed = String(active.id).includes('__')
          // Measure the grabbed element for both the grab offset (so the drop
          // preserves where it was held) and the overlay size (keep block size).
          let offset = 0
          let w = 0
          let h = 0
          if (activatorEvent instanceof PointerEvent) {
            const el = (activatorEvent.target as HTMLElement | null)?.closest('button')
            const rect = el?.getBoundingClientRect()
            if (rect) {
              offset = activatorEvent.clientY - rect.top
              w = rect.width
              h = rect.height
            } else if (active.rect.current.initial) {
              offset = activatorEvent.clientY - active.rect.current.initial.top
            }
          }
          grabOffsetRef.current = offset
          setActiveDrag(task ? { task, timed, w, h } : null)
        }}
        onDragMove={({ active, over }) => {
          // Only day *columns* ('day:') take a snapped time preview — the all-day
          // row ('ad:') and the tray don't.
          const onDay = !!over && String(over.id).startsWith('day:')
          const habit = isHabitDrag(active.id)
          // Habits recur, so the past-date/time restriction doesn't apply.
          const blocked = onDay && !habit && isPastDrop(String(over!.id), over!.rect.top)
          setCursorBlocked(blocked)
          if (onDay && isTimeGrid && !blocked) {
            setDropSlot({
              key: String(over!.id),
              minutes: dropMinutesFor(over!.rect.top),
              durationMin: findTask(active.id)?.durationMinutes ?? 60,
            })
          } else if (dropSlot) {
            setDropSlot(null)
          }
        }}
        onDragCancel={() => {
          setActiveDrag(null)
          setDropSlot(null)
          setCursorBlocked(false)
        }}
        onDragEnd={({ active, over }) => {
          setActiveDrag(null)
          setDropSlot(null)
          setCursorBlocked(false)
          if (!over) return
          const taskId = taskIdOf(active.id) as Id<'nodes'>
          const habit = isHabitDrag(active.id)
          // Dropped back on the tray → clear the due date (habits can't unschedule).
          if (over.id === TRAY_DROPPABLE) {
            if (!habit) onUnschedule?.(taskId)
            return
          }
          // Dropped on the all-day row → flag all-day (no time). One-off tasks
          // also take the day as their due date; past days are disallowed.
          if (String(over.id).startsWith(ALL_DAY_PREFIX)) {
            const day = parseAllDayKey(String(over.id))
            if (!habit && day.getTime() < today.getTime()) return
            onSetAllDay?.(taskId, day)
            return
          }
          // Habits: drop in a week/day column updates recurrence time (+ day).
          if (habit) {
            if (isTimeGrid) {
              onRescheduleHabit?.(
                taskId,
                parseDayKey(String(over.id)),
                dropMinutesFor(over.rect.top),
              )
            }
            return
          }
          if (!onSchedule) return
          // Past drops aren't allowed: scheduled tasks clamp to today / now,
          // tray tasks return to the tray (resolveDrop → null, no schedule).
          const drop = resolveDrop(active.id, String(over.id), over.rect.top)
          if (!drop) return
          onSchedule(taskId, drop.day, drop.minutes ?? undefined)
        }}
      >
        <div className={cn('flex flex-col', cursorBlocked && '[&_*]:!cursor-not-allowed')}>
          {/* Toolbar — sticks to the top of the outer inbox scroller */}
          <div
            className={cn(
              'sticky z-40 flex h-11 items-center justify-between gap-2 border-b border-border/40 bg-background px-4 shrink-0',
              toolbarTop,
            )}
            // Keep toolbar interactions out of the calendar's drag/swipe surface.
            onPointerDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
          >
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label={t('inbox.calendarPrev')}
                title={t('inbox.calendarPrev')}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setCurrentDate(today)}
                aria-label={t('inbox.calendarToday')}
                title={t('inbox.calendarToday')}
                className="min-w-[96px] text-center sm:min-w-[120px] text-sm font-semibold capitalize hover:text-primary-text transition-colors cursor-pointer"
              >
                {headerLabel}
              </button>
              <button
                type="button"
                onClick={() => navigate(1)}
                aria-label={t('inbox.calendarNext')}
                title={t('inbox.calendarNext')}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="flex items-center gap-1.5">
              {allGoals && (
                <button
                  type="button"
                  aria-pressed={allGoals.on}
                  onClick={() => allGoals.onChange(!allGoals.on)}
                  title={t(allGoals.on ? 'inbox.calendarAllGoalsOn' : 'inbox.calendarAllGoalsOff')}
                  className={cn(
                    'flex h-7 cursor-pointer items-center gap-1 rounded-md border px-1.5 text-xs font-medium transition-colors',
                    allGoals.on
                      ? 'border-primary/40 bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <Layers className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{t('inbox.calendarAllGoals')}</span>
                </button>
              )}
              <div className="flex items-center overflow-hidden rounded-md border border-border divide-x divide-border">
                {(['month', 'week', 'day'] as CalendarView[]).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setView(v)}
                    title={t(`inbox.calendarView.${v}`)}
                    className={cn(
                      'cursor-pointer px-3 h-7 text-xs font-medium transition-colors',
                      view === v
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    {t(`inbox.calendarView.${v}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Body — month fills a screen; week/day flow at natural height so the
          outer inbox container is the only scroller. */}
          <div className={cn('flex flex-col', view === 'month' && 'min-h-svh')}>
            {view === 'month' && (
              <MonthView
                year={year}
                month={month}
                tasks={tasks}
                today={today}
                onComplete={onComplete}
                onUncomplete={onUncomplete}
                onDetail={onDetail}
                reschedulable={!!onSchedule}
              />
            )}
            {view === 'week' && (
              <WeekView
                weekStart={weekStart}
                tasks={tasks}
                today={today}
                onComplete={onComplete}
                onUncomplete={onUncomplete}
                onDetail={onDetail}
                onDurationChange={onDurationChange}
                reschedulable={!!onSchedule}
                allDayDroppable={!!onSetAllDay}
                dropSlot={dropSlot}
                dayHeaderTop={dayHeaderTop}
              />
            )}
            {view === 'day' && (
              <DayView
                day={currentDate}
                tasks={tasks}
                today={today}
                onComplete={onComplete}
                onUncomplete={onUncomplete}
                onDetail={onDetail}
                onDurationChange={onDurationChange}
                reschedulable={!!onSchedule}
                allDayDroppable={!!onSetAllDay}
                dropSlot={dropSlot}
                dayHeaderTop={dayHeaderTop}
              />
            )}
          </div>

          {unscheduled && trayHost ? (
            createPortal(
              <UnscheduledTray
                unscheduled={unscheduled}
                onDetail={onDetail}
                habitDrag={
                  !!activeDrag && !!(activeDrag.task.recursPeriod || activeDrag.task.recurs)
                }
                rail
              />,
              trayHost,
            )
          ) : unscheduled &&
            (unscheduled.length > 0 ||
              (activeDrag && !(activeDrag.task.recursPeriod || activeDrag.task.recurs))) ? (
            <UnscheduledTray
              unscheduled={unscheduled}
              onDetail={onDetail}
              habitDrag={!!activeDrag && !!(activeDrag.task.recursPeriod || activeDrag.task.recurs)}
            />
          ) : (
            undated > 0 &&
            view !== 'day' && (
              <div className="shrink-0 border-t border-border/40 px-4 py-1.5">
                <p className="text-[10px] text-muted-foreground/50">
                  {t('inbox.calendarUndated', { count: undated })}
                </p>
              </div>
            )
          )}
        </div>
        <DragOverlay dropAnimation={null}>
          {activeDrag ? (
            // Timed blocks keep their real footprint (size captured at grab);
            // tray/month chips use a compact label.
            activeDrag.timed && activeDrag.w > 0 ? (
              <div
                style={{ width: activeDrag.w, height: activeDrag.h }}
                className="cursor-grabbing overflow-hidden rounded-md border border-primary bg-primary/25 px-1.5 py-1 text-[11px] font-medium leading-snug shadow-lg"
              >
                {activeDrag.task.title}
              </div>
            ) : (
              <div className="max-w-[180px] cursor-grabbing truncate rounded-md border border-primary bg-background px-2 py-1 text-xs font-medium shadow-lg">
                {activeDrag.task.title}
              </div>
            )
          ) : null}
        </DragOverlay>
      </DndContext>
    </CompletionDatesContext.Provider>
  )
}
